
/**
 * Retail cart. FR-07 leaves the storage choice to the implementer because
 * checkout re-prices from `published_products` regardless, so a persisted
 * client cart is the boring correct answer: no round trip to add a line, no
 * server session to reap, and nothing here is trusted at the money boundary.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { CartLine } from '@/lib/types';

const STORAGE_KEY = 'shopace.cart.v1';

interface CartApi {
  lines: CartLine[];
  count: number;
  ready: boolean;
  add: (line: CartLine) => void;
  setQty: (publishedId: string, size: string, qty: number) => void;
  remove: (publishedId: string, size: string) => void;
  clear: () => void;
}

const CartContext = createContext<CartApi | null>(null);

function read(): CartLine[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setLines(read());
    setReady(true);
  }, []);

  const persist = useCallback((next: CartLine[]) => {
    setLines(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Private browsing with storage disabled: the cart stays in memory.
    }
  }, []);

  const api = useMemo<CartApi>(
    () => ({
      lines,
      ready,
      count: lines.reduce((sum, l) => sum + l.qty, 0),
      add: (line) => {
        const existing = lines.find(
          (l) => l.published_id === line.published_id && l.size === line.size,
        );
        persist(
          existing
            ? lines.map((l) =>
                l === existing ? { ...l, qty: Math.min(999, l.qty + line.qty) } : l,
              )
            : [...lines, line],
        );
      },
      setQty: (publishedId, size, qty) => {
        if (qty < 1) {
          persist(
            lines.filter((l) => !(l.published_id === publishedId && l.size === size)),
          );
          return;
        }
        persist(
          lines.map((l) =>
            l.published_id === publishedId && l.size === size
              ? { ...l, qty: Math.min(999, qty) }
              : l,
          ),
        );
      },
      remove: (publishedId, size) =>
        persist(lines.filter((l) => !(l.published_id === publishedId && l.size === size))),
      clear: () => persist([]),
    }),
    [lines, ready, persist],
  );

  return <CartContext.Provider value={api}>{children}</CartContext.Provider>;
}

export function useCart(): CartApi {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
